import TomSelect from "../../js/tom-select/tom-select.0c9bda91e62b.js";
import { SPECIFIED_NO_INSURANCE_VALUE } from "../../js/shared.0f27bac2f95c.js";
import { createOptionRenderer } from "./pet-tomselect-utils.8c37471ed68f.js";

const alertIcon = `
<svg aria-hidden="true" class="tqc-search-error pv-icon-20 pv-text-critical" style="flex-shrink: 0; margin-bottom: 4px">
  <use xlink:href="#alert-circle"></use>
</svg>
`;

class PetSearchBarValidator extends HTMLElement {
  ERROR_BACKGROUND_SHADE_CLASSNAME = "pv-surface-accent";

  constructor() {
    super();
    this.selectedService = null;
    this.selectedLocation = null;
    this.selectedInsurance = null;
  }

  connectedCallback() {
    this.addEventListener("change", this.handleFieldChanged);
    this.addEventListener("submit", this.handleSubmit);
    document.addEventListener("htmx:afterSettle", this.handleAfterSettle);
  }

  disconnectedCallback() {
    document.removeEventListener("htmx:afterSettle", this.handleAfterSettle);
  }

  // Accessing the form through this getter means we always have a current
  // reference, even through an htmx swap.
  get searchForm() {
    return this.querySelector("#rates-form");
  }

  get serviceSelector() {
    return this.querySelector("#service_code").tomselect;
  }

  get locationSelector() {
    return this.querySelector("#care-location").tomselect;
  }

  get insuranceSelector() {
    return this.querySelector("#pet-insurance").tomselect;
  }

  get submitButtons() {
    return this.searchForm.querySelectorAll("button[type='submit']");
  }

  get fieldContainers() {
    return {
      service: this.querySelector("#service_code").parentElement.parentElement,
      location:
        this.querySelector("#care-location").parentElement.parentElement
          .parentElement,
      insurance:
        this.querySelector("#pet-insurance").parentElement.parentElement
          .parentElement,
    };
  }

  optionForCurrentTomSelectValue = (selector) => {
    const key = selector.getValue();
    if (!key) return null;
    const option = selector.options[key];
    if (!option) {
      throw new Error(`no option with key ${key}`);
    }
    return option;
  };

  handleFieldChanged = (e) => {
    switch (e.target.id) {
      case "service_code":
        this.handleServiceChanged();
        break;
      case "care-location":
        this.handleLocationChanged();
        break;
      case "pet-insurance":
        this.handleInsuranceChanged();
        break;
    }
  };

  // After htmx swap settles, Tom Select instances need to be re-synced with
  // the values in the HTML document.
  handleAfterSettle = () => {
    this.clearError();
    this.selectedService = this.optionForCurrentTomSelectValue(
      this.serviceSelector,
    );
    this.selectedLocation = this.optionForCurrentTomSelectValue(
      this.locationSelector,
    );
    this.selectedInsurance = this.optionForCurrentTomSelectValue(
      this.insuranceSelector,
    );
    this.updateAllowedLocations();
    this.revalidate();
  };

  setSubmitDisabled = (isDisabled) => {
    for (const button of this.submitButtons) {
      button.disabled = isDisabled;
    }
  };

  revalidate = () => {
    const isValid = this.validate();
    this.setSubmitDisabled(!isValid);
    return isValid;
  };

  handleSubmit = (e) => {
    this.clearError();
    if (!this.revalidate()) {
      e.preventDefault();
    }
  };

  clearError = () => {
    for (let element of Object.values(this.fieldContainers)) {
      element.classList.remove(this.ERROR_BACKGROUND_SHADE_CLASSNAME);
    }
    document.querySelectorAll(".tqc-search-error").forEach((el) => {
      el.remove();
    });
  };

  showFieldErrorState = (fieldName) => {
    const field = this.fieldContainers[fieldName];
    if (!field) return;
    field.insertAdjacentHTML("beforeend", alertIcon);
    field.classList.add(this.ERROR_BACKGROUND_SHADE_CLASSNAME);
  };

  addError = (message, fieldName) => {
    const desktopError =
      "<div class='tqc-search-error pv-hide-on-small pv-text-critical pv-text-body-xs' style='margin-top: 8px'>" +
      message +
      "</div>";
    const mobileError =
      "<div class='tqc-search-error pv-hide-on-large search-bar__group pv-text-critical pv-text-body-xs'>" +
      message +
      "</div>";
    this.searchForm.insertAdjacentHTML("afterend", desktopError);
    this.fieldContainers[fieldName].insertAdjacentHTML("afterend", mobileError);
  };

  resetLocations = () => {
    this.locationSelector.clearCache();
    for (const [_locationId, location] of Object.entries(
      this.locationSelector.options,
    )) {
      location.disabled = false;
    }
    this.locationSelector.refreshOptions(false);
  };

  disableInvalidLocations = (validLocations) => {
    this.locationSelector.clearCache();
    for (const [locationId, location] of Object.entries(
      this.locationSelector.options,
    )) {
      if (validLocations.has(parseInt(locationId))) {
        location.disabled = false;
      } else {
        location.disabled = true;
      }
    }
    this.locationSelector.refreshOptions(false);
  };

  handleLocationChanged = () => {
    this.clearError();
    this.selectedLocation = this.optionForCurrentTomSelectValue(
      this.locationSelector,
    );
    this.revalidate();
  };

  handleServiceChanged = () => {
    this.clearError();
    this.selectedService = this.optionForCurrentTomSelectValue(
      this.serviceSelector,
    );
    this.updateAllowedLocations();
    this.revalidate();
  };

  handleInsuranceChanged = () => {
    this.clearError();
    this.selectedInsurance = this.optionForCurrentTomSelectValue(
      this.insuranceSelector,
    );
    this.updateAllowedLocations();
    if (this.revalidate()) {
      document.dispatchEvent(
        new CustomEvent("tqc:pet-insurance-changed", {
          detail: { networkConfig: this.selectedInsurance },
          bubbles: true,
        }),
      );
    }
  };

  validateUnsupportedSelections = () => {
    const isUnsupportedNetworkSelected =
      this.selectedInsurance?.is_unsupported_for_ssp;
    if (isUnsupportedNetworkSelected) {
      const isSspSelected = this.selectedService?.type == "package";
      if (isSspSelected) {
        return false;
      }
    }

    return true;
  };

  get providerIdsOfferingService() {
    return this.selectedService?.provider_ids ?? null;
  }

  get providerIdsAcceptingInsurance() {
    if (!this.selectedInsurance) return null;
    if (this.selectedInsurance.value === SPECIFIED_NO_INSURANCE_VALUE)
      return null;
    return this.selectedInsurance.provider_ids ?? null;
  }

  updateAllowedLocations = () => {
    const { providerIdsOfferingService, providerIdsAcceptingInsurance } = this;

    if (!providerIdsOfferingService && !providerIdsAcceptingInsurance) {
      this.resetLocations();
      return;
    }

    let allowedProviders;
    if (!providerIdsOfferingService) {
      allowedProviders = new Set(providerIdsAcceptingInsurance);
    } else if (!providerIdsAcceptingInsurance) {
      allowedProviders = new Set(providerIdsOfferingService);
    } else {
      allowedProviders = new Set(
        providerIdsOfferingService.filter((id) =>
          providerIdsAcceptingInsurance.includes(id),
        ),
      );
    }
    this.disableInvalidLocations(allowedProviders);
  };

  validate = () => {
    const validUnsupportedSelection = this.validateUnsupportedSelections();
    if (!validUnsupportedSelection) {
      this.addError(
        "Service packages are not supported for this plan. Please select a procedure or proceed without insurance to view cash prices.",
        "service",
      );
      this.showFieldErrorState("service");
      return false;
    }

    const selectedProviderId = parseInt(this.locationSelector.getValue());
    if (isNaN(selectedProviderId)) {
      throw new Error(
        "pet location input contained missing or non-provider-id options",
      );
    }

    const { providerIdsOfferingService, providerIdsAcceptingInsurance } = this;

    const validBasedOnService =
      !providerIdsOfferingService ||
      providerIdsOfferingService.includes(selectedProviderId);
    const validBasedOnInsurance =
      !providerIdsAcceptingInsurance ||
      providerIdsAcceptingInsurance.includes(selectedProviderId);
    if (validBasedOnService && validBasedOnInsurance) {
      return true;
    }

    if (!validBasedOnInsurance) {
      this.addError(
        "This location does not support your selected insurance. Please choose another location or proceed without insurance.",
        "location",
      );
    }

    if (!validBasedOnService) {
      this.addError(
        "This location does not support your selected service. Please select a different location to continue.",
        "location",
      );
    }

    this.showFieldErrorState("location");
    return false;
  };
}

window.customElements.define("pet-search-bar-validator", PetSearchBarValidator);
